import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store } from "lucide-react";

export default function RecordAdvanceDialog({ open, onOpenChange, customer, onDone }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [shopId, setShopId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);

  useEffect(() => {
    if (open) {
      setAmount("");
      setMode("cash");
      setShopId("");
      setNotes("");
      // Load shops
      api.get("/shops").then(({ data }) => {
        setShops(data.items || []);
        if (data.items?.length === 1) {
          setShopId(data.items[0].id);
        }
      }).catch(() => setShops([]));
    }
  }, [open]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!shopId) return toast.error("Please select a shop");
    setBusy(true);
    try {
      await api.post(`/credit/${customer.id}/advance`, { amount: amt, payment_mode: mode, shop_id: shopId, notes });
      toast.success(`Advance of ₹${amt.toFixed(2)} recorded`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-advance-dialog">
        <DialogHeader>
          <DialogTitle>Record Advance Payment</DialogTitle>
          <DialogDescription>
            Money received from <span className="font-medium">{customer?.name}</span> for future purchases.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Store className="w-3 h-3" /> Shop *
            </Label>
            <Select value={shopId} onValueChange={setShopId}>
              <SelectTrigger className="mt-1" data-testid="advance-shop-select">
                <SelectValue placeholder="Select shop that received payment" />
              </SelectTrigger>
              <SelectContent>
                {shops.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              This will reflect in the selected shop's financials
            </p>
          </div>
          <div>
            <Label className="text-xs">Amount *</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 font-mono-data"
              placeholder="0.00"
              data-testid="advance-amount-input"
            />
          </div>
          <div>
            <Label className="text-xs">Payment Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1" data-testid="advance-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" rows={2} placeholder="Optional" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !amount || !shopId} style={{ backgroundColor: "#9E6D5B" }} className="text-white" data-testid="advance-submit">
            {busy ? "Saving…" : "Record Advance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
